import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LAWYER_LOGIN_PATH } from "@lawyer/constants/route";

/**
 * Auth guard for the Lawyer Portal.
 *
 *   - Loading → neutral white screen (no portal branding flash for non-lawyers)
 *   - Unauthenticated → redirect to portal login (own URL)
 *   - Authenticated non-lawyer → silent redirect to "/" (no leak)
 *   - Authenticated lawyer with no `lawyer_id` (account exists but isn't linked
 *     to a lawyer profile yet) → friendly "pending verification" state
 *   - Verified lawyer → render children
 *
 * Mirrors `RequirePermission` from the admin app for consistency.
 */
export default function RequireLawyer({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Inject noindex on every portal route — partner-only surface, not for SEO.
  React.useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name", "robots"); document.head.appendChild(meta); }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", "noindex,nofollow,noarchive,nosnippet");
    return () => { if (prev != null) meta.setAttribute("content", prev); };
  }, []);

  if (loading) return <div className="min-h-screen bg-white" data-testid="lawyer-auth-resolving" />;
  if (!user) return <Navigate to={LAWYER_LOGIN_PATH} replace state={{ from: location.pathname }} />;
  if (user.role !== "lawyer") return <Navigate to="/" replace />;
  return children;
}
